import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import {
  customFormatFileNameAtom,
  gDriveDataAtom,
  nodeTitleAtom,
  presentationsTreeAtom,
  presentationsTreeIdMap,
} from './atoms'
import { useCallback } from 'react'
import { useResetTree } from '../../../reducers/trees_hooks'
import { idsForNode, UpdateMethod, UpdateType } from '../nodeUpdates/utility'
import {
  getFileIndexMapping,
  getNodeForFile,
  getNodeForMapping,
  getPresentationUpdates,
  getPresentationUpdatesForFile,
  presentationListForNode,
} from './helpers'
import { PresentationFile } from './types'
import { openNode } from '../../utility/TreeNavigation'
import { DeleteNodeUpdateData, UpdateData } from '../nodeUpdates/types'

export function useResetPresentationsTree() {
  const tree = useAtomValue(presentationsTreeAtom)
  const resetTree = useResetTree()
  return useCallback(() => {
    if (!tree) return
    resetTree(idsForNode(tree))
  }, [tree, resetTree])
}

export function useFileIndexesForTypes() {
  const fileFormat = useAtomValue(customFormatFileNameAtom)
  return useCallback(
    (fileName: string) => {
      return getFileIndexMapping({ fileName, fileFormat })
    },
    [fileFormat],
  )
}

export function useNodeForFile() {
  const tree = useAtomValue(presentationsTreeAtom)
  const fileFormat = useAtomValue(customFormatFileNameAtom)
  return useCallback(
    (file: PresentationFile) => {
      if (!tree) return
      return getNodeForFile(file, fileFormat, tree)
    },
    [tree, fileFormat],
  )
}

export function useGetUpdatesForFile() {
  const customFormatFileName = useAtomValue(customFormatFileNameAtom)
  const tree = useAtomValue(presentationsTreeAtom)
  const presentationListTitle = useAtomValue(nodeTitleAtom)

  return useCallback(
    (file: { id: string; name: string }): UpdateData[] => {
      if (!tree) return []
      return getPresentationUpdatesForFile(file, tree, {
        customFormatFileName,
        presentationListTitle,
      })
    },
    [customFormatFileName, presentationListTitle, tree],
  )
}

export function useAllPresentationsUpdates() {
  const gDriveData = useAtomValue(gDriveDataAtom)
  const tree = useAtomValue(presentationsTreeAtom)
  const customFormatFileName = useAtomValue(customFormatFileNameAtom)
  const presentationListTitle = useAtomValue(nodeTitleAtom)
  if (!tree) return []
  return getPresentationUpdates(gDriveData, tree, {
    presentationListTitle,
    customFormatFileName,
  })
}

export function useDeletePresentationForFIle() {
  const getNode = useNodeForFile()
  const presentationListTitle = useAtomValue(nodeTitleAtom)

  return useCallback(
    (file: PresentationFile) => {
      const lesson = getNode(file)
      const presentationList =
        lesson && presentationListForNode(lesson, presentationListTitle)

      const deleteParams: DeleteNodeUpdateData | undefined =
        presentationList && {
          method: UpdateMethod.delete,
          type: UpdateType.node,
          ids: idsForNode(presentationList),
        }
      return {
        hasLesson: !!lesson,
        deleteParams,
      }
    },
    [getNode, presentationListTitle],
  )
}

export function useNavigateForMapping() {
  const tree = useAtomValue(presentationsTreeAtom)
  const idMap = useAtomValue(presentationsTreeIdMap)
  return useCallback(
    (typePart: string, mapping: [string, string | undefined][]) => {
      if (!tree) return {}
      const dest = mapping.find((m) => {
        return m[0] === typePart
      })
      if (!dest) return {}
      const filteredMapping = mapping.slice(0, mapping.indexOf(dest) + 1)
      const destinationNode = getNodeForMapping(filteredMapping, tree)
      return {
        canNavigate: !!destinationNode,
        doNavigate: () => {
          if (!destinationNode) return
          openNode(destinationNode, idMap)
        },
      }
    },
    [tree, idMap],
  )
}
